/**
 * PerformanceProfiler - Sistema de profiling para medir tiempos de carga de cuadrantes
 * 
 * Este módulo permite medir y registrar el tiempo que tarda cada función
 * en el proceso de carga de cuadrantes, distinguiendo entre:
 * - Primera carga (sin cache)
 * - Cargas subsecuentes (con cache)
 */

class PerformanceProfiler {
  constructor() {
    this.measurements = new Map();
    this.sessions = [];
    this.currentSession = null;
    this.isFirstLoad = true; // Flag para identificar primera carga
  }

  /**
   * Inicia una nueva sesión de profiling
   * @param {string} sessionId - Identificador único de la sesión
   * @param {Object} metadata - Metadata adicional (tipo de búsqueda, parámetros, etc.)
   */
  startSession(sessionId, metadata = {}) {
    // Determinar el tipo de carga basado en metadata
    const esCuadranteNuevo = metadata.esCuadranteNuevo || false;
    const esCreacionNueva = metadata.tipo === 'creacion_nuevo_cuadrante' || false;
    const tipoRealCarga = esCuadranteNuevo || esCreacionNueva ? true : this.isFirstLoad;
    
    console.log(`📊 [PROFILING] Iniciando sesión: ${sessionId}`);
    
    if (esCreacionNueva) {
      console.log(`   Tipo: 🆕 CREACIÓN DE CUADRANTE NUEVO (desde Freematica)`);
    } else if (esCuadranteNuevo) {
      console.log(`   Tipo: 🆕 CUADRANTE RECIÉN CREADO (primera visualización)`);
    } else if (this.isFirstLoad) {
      console.log(`   Tipo: 🔵 PRIMERA CARGA (en esta sesión del navegador)`);
    } else {
      console.log(`   Tipo: 🔄 CARGA SUBSECUENTE (cuadrante existente)`);
    }
    
    this.currentSession = {
      sessionId,
      startTime: performance.now(),
      endTime: null,
      totalTime: null,
      measurements: [],
      metadata: {
        ...metadata,
        isFirstLoad: tipoRealCarga,
        esCuadranteNuevo: esCuadranteNuevo,
        esCreacionNueva: esCreacionNueva,
        timestamp: new Date().toISOString(),
      },
    };
  }

  /**
   * Inicia una medición individual
   * @param {string} functionName - Nombre de la función a medir
   * @param {Object} params - Parámetros de la función
   */
  startMeasurement(functionName, params = {}) {
    if (!this.currentSession) {
      console.warn(`⚠️ [PROFILING] No hay sesión activa para medir: ${functionName}`);
      return null;
    }

    const measurementId = `${functionName}_${Date.now()}`;
    const measurement = {
      measurementId,
      functionName,
      startTime: performance.now(),
      endTime: null,
      duration: null,
      params,
      result: null,
      error: null,
    };

    this.measurements.set(measurementId, measurement);
    console.log(`⏱️ [PROFILING] ⏯️ Iniciando: ${functionName}`);
    
    return measurementId;
  }

  /**
   * Finaliza una medición individual
   * @param {string} measurementId - ID de la medición
   * @param {Object} result - Resultado de la función (opcional)
   */
  endMeasurement(measurementId, result = null) {
    if (!measurementId || !this.measurements.has(measurementId)) {
      console.warn(`⚠️ [PROFILING] Medición no encontrada: ${measurementId}`);
      return;
    }

    const measurement = this.measurements.get(measurementId);
    measurement.endTime = performance.now();
    measurement.duration = measurement.endTime - measurement.startTime;
    measurement.result = result;

    // Agregar a la sesión actual
    if (this.currentSession) {
      this.currentSession.measurements.push(measurement);
    }

    console.log(`⏱️ [PROFILING] ⏹️ Finalizado: ${measurement.functionName}`);
    console.log(`   ⏰ Duración: ${measurement.duration.toFixed(2)}ms`);

    return measurement;
  }

  /**
   * Registra un error en una medición
   * @param {string} measurementId - ID de la medición
   * @param {Error} error - Error ocurrido
   */
  recordError(measurementId, error) {
    if (!measurementId || !this.measurements.has(measurementId)) {
      console.warn(`⚠️ [PROFILING] Medición no encontrada para error: ${measurementId}`);
      return;
    }

    const measurement = this.measurements.get(measurementId);
    measurement.endTime = performance.now();
    measurement.duration = measurement.endTime - measurement.startTime;
    measurement.error = {
      message: error.message,
      stack: error.stack,
    };

    // Agregar a la sesión actual
    if (this.currentSession) {
      this.currentSession.measurements.push(measurement);
    }

    console.log(`⏱️ [PROFILING] ❌ Error en: ${measurement.functionName}`);
    console.log(`   ⏰ Duración hasta error: ${measurement.duration.toFixed(2)}ms`);
    console.log(`   ❌ Error: ${error.message}`);
  }

  /**
   * Finaliza la sesión de profiling actual
   */
  endSession() {
    if (!this.currentSession) {
      console.warn(`⚠️ [PROFILING] No hay sesión activa para finalizar`);
      return null;
    }

    this.currentSession.endTime = performance.now();
    this.currentSession.totalTime = this.currentSession.endTime - this.currentSession.startTime;

    // Agregar a historial de sesiones
    this.sessions.push(this.currentSession);

    console.log(`\n📊 ================================================`);
    console.log(`📊 [PROFILING] RESUMEN DE SESIÓN: ${this.currentSession.sessionId}`);
    console.log(`📊 ================================================`);
    console.log(`⏰ Tiempo total: ${this.currentSession.totalTime.toFixed(2)}ms`);
    
    // Determinar y mostrar el tipo de carga
    let tipoCarga = 'CARGA SUBSECUENTE (CACHED)';
    if (this.currentSession.metadata.esCreacionNueva) {
      tipoCarga = '🆕 CREACIÓN DE CUADRANTE NUEVO (desde Freematica)';
    } else if (this.currentSession.metadata.esCuadranteNuevo) {
      tipoCarga = '🆕 CUADRANTE RECIÉN CREADO (primera visualización)';
    } else if (this.currentSession.metadata.isFirstLoad) {
      tipoCarga = '🔵 PRIMERA CARGA (en esta sesión)';
    }
    
    console.log(`🔄 Tipo de carga: ${tipoCarga}`);
    console.log(`📅 Timestamp: ${this.currentSession.metadata.timestamp}`);
    console.log(`\n📋 Desglose por función:`);
    console.log(`------------------------------------------------`);

    // Ordenar mediciones por orden de ejecución
    const sortedMeasurements = [...this.currentSession.measurements].sort(
      (a, b) => a.startTime - b.startTime
    );

    sortedMeasurements.forEach((measurement, index) => {
      const percentage = ((measurement.duration / this.currentSession.totalTime) * 100).toFixed(1);
      const status = measurement.error ? '❌' : '✅';
      console.log(
        `${index + 1}. ${status} ${measurement.functionName.padEnd(40)} ${measurement.duration.toFixed(2).padStart(10)}ms (${percentage}%)`
      );
    });

    console.log(`------------------------------------------------`);
    console.log(`📊 Total de mediciones: ${sortedMeasurements.length}`);
    console.log(`📊 ================================================\n`);

    // Marcar que ya no es primera carga
    if (this.isFirstLoad) {
      this.isFirstLoad = false;
    }

    const completedSession = this.currentSession;
    this.currentSession = null;
    
    return completedSession;
  }

  /**
   * Obtiene el reporte de la sesión actual o la última completada
   */
  getCurrentReport() {
    if (this.currentSession) {
      return {
        ...this.currentSession,
        status: 'in_progress',
      };
    }

    if (this.sessions.length > 0) {
      return {
        ...this.sessions[this.sessions.length - 1],
        status: 'completed',
      };
    }

    return null;
  }

  /**
   * Obtiene todas las sesiones registradas
   */
  getAllSessions() {
    return this.sessions;
  }

  /**
   * Compara dos sesiones (útil para comparar primera carga vs cached)
   * @param {string} sessionId1 - ID de la primera sesión
   * @param {string} sessionId2 - ID de la segunda sesión
   */
  compareSessions(sessionId1, sessionId2) {
    const session1 = this.sessions.find(s => s.sessionId === sessionId1);
    const session2 = this.sessions.find(s => s.sessionId === sessionId2);

    if (!session1 || !session2) {
      console.error('❌ Una o ambas sesiones no encontradas');
      return null;
    }

    console.log(`\n📊 ================================================`);
    console.log(`📊 [PROFILING] COMPARACIÓN DE SESIONES`);
    console.log(`📊 ================================================`);
    console.log(`Sesión 1: ${session1.sessionId} (${session1.metadata.isFirstLoad ? 'PRIMERA CARGA' : 'CACHED'})`);
    console.log(`Sesión 2: ${session2.sessionId} (${session2.metadata.isFirstLoad ? 'PRIMERA CARGA' : 'CACHED'})`);
    console.log(`\n⏰ Tiempos totales:`);
    console.log(`   Sesión 1: ${session1.totalTime.toFixed(2)}ms`);
    console.log(`   Sesión 2: ${session2.totalTime.toFixed(2)}ms`);
    console.log(`   Diferencia: ${Math.abs(session1.totalTime - session2.totalTime).toFixed(2)}ms`);
    
    const faster = session1.totalTime < session2.totalTime ? 'Sesión 1' : 'Sesión 2';
    const improvement = Math.abs((1 - Math.min(session1.totalTime, session2.totalTime) / Math.max(session1.totalTime, session2.totalTime)) * 100).toFixed(1);
    console.log(`   Más rápida: ${faster} (${improvement}% mejora)`);

    console.log(`\n📋 Comparación por función:`);
    console.log(`------------------------------------------------`);

    // Agrupar mediciones por nombre de función
    const functionMap = new Map();

    session1.measurements.forEach(m => {
      if (!functionMap.has(m.functionName)) {
        functionMap.set(m.functionName, { session1: null, session2: null });
      }
      functionMap.get(m.functionName).session1 = m;
    });

    session2.measurements.forEach(m => {
      if (!functionMap.has(m.functionName)) {
        functionMap.set(m.functionName, { session1: null, session2: null });
      }
      functionMap.get(m.functionName).session2 = m;
    });

    functionMap.forEach((measurements, functionName) => {
      const time1 = measurements.session1?.duration || 0;
      const time2 = measurements.session2?.duration || 0;
      const diff = time2 - time1;
      const diffSign = diff > 0 ? '+' : '';
      
      console.log(`\n${functionName}:`);
      console.log(`   Sesión 1: ${time1.toFixed(2)}ms`);
      console.log(`   Sesión 2: ${time2.toFixed(2)}ms`);
      console.log(`   Diferencia: ${diffSign}${diff.toFixed(2)}ms`);
    });

    console.log(`📊 ================================================\n`);

    return {
      session1,
      session2,
      comparison: {
        totalTimeDiff: session2.totalTime - session1.totalTime,
        improvement: improvement,
        faster: faster,
      },
    };
  }

  /**
   * Exporta los datos de profiling a JSON
   */
  exportToJSON() {
    return {
      sessions: this.sessions,
      currentSession: this.currentSession,
      isFirstLoad: this.isFirstLoad,
      exportedAt: new Date().toISOString(),
    };
  }

  /**
   * Limpia todas las mediciones y sesiones
   */
  reset() {
    console.log('🔄 [PROFILING] Limpiando datos de profiling...');
    this.measurements.clear();
    this.sessions = [];
    this.currentSession = null;
    this.isFirstLoad = true;
  }

  /**
   * Resetea el flag de primera carga (útil para testing)
   */
  resetFirstLoadFlag() {
    console.log('🔄 [PROFILING] Reseteando flag de primera carga...');
    this.isFirstLoad = true;
  }

  /**
   * Helper: Wrapper para medir funciones asíncronas automáticamente
   * @param {string} functionName - Nombre de la función
   * @param {Function} asyncFunction - Función asíncrona a ejecutar
   * @param {Object} params - Parámetros de la función
   */
  async measureAsync(functionName, asyncFunction, params = {}) {
    const measurementId = this.startMeasurement(functionName, params);
    
    try {
      const result = await asyncFunction();
      this.endMeasurement(measurementId, { success: true, dataSize: JSON.stringify(result).length });
      return result;
    } catch (error) {
      this.recordError(measurementId, error);
      throw error;
    }
  }

  /**
   * Helper: Wrapper para medir funciones síncronas automáticamente
   * @param {string} functionName - Nombre de la función
   * @param {Function} syncFunction - Función síncrona a ejecutar
   * @param {Object} params - Parámetros de la función
   */
  measureSync(functionName, syncFunction, params = {}) {
    const measurementId = this.startMeasurement(functionName, params);
    
    try {
      const result = syncFunction();
      this.endMeasurement(measurementId, { success: true });
      return result;
    } catch (error) {
      this.recordError(measurementId, error);
      throw error;
    }
  }
}

// Instancia global del profiler
const profiler = new PerformanceProfiler();

// Exportar tanto la clase como la instancia
export default profiler;
export { PerformanceProfiler };

